import {CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  isDevMode,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {MobileBridgeService} from '@gatewee/services/core/mobile-bridge.service';
import {ProfileSyncService} from '@gatewee/services/profile-sync.service';
import {MobileBridgeEvent} from '@gatewee/types/mobile';
import {Recipient} from '@gatewee/types/recipient';
import {PublicSettings} from '@gatewee/types/system';
import {TaskWithProgress} from '@gatewee/types/task';
import {getTaskLabel} from '@gatewee/utils/helpers/get-task-label';
import {
  isSupportedLanguage,
  languageOptions as i18nLanguageOptions,
  resolveLanguage,
  SupportedLanguageCode,
} from '@gatewee/utils/helpers/supported-languages';
import {TranslateService} from '@ngx-translate/core';
import {TranslateModule} from '@ngx-translate/core';
import {TuiButton, TuiDataList, TuiDropdown} from '@taiga-ui/core';
import {TuiAutoColorPipe} from '@taiga-ui/core/pipes/auto-color';
import {TuiInitialsPipe} from '@taiga-ui/core/pipes/initials';
import {TuiAvatar} from '@taiga-ui/kit/components/avatar';
import {GateweeIconComponent} from '../../gatewee-icon/gatewee-icon.component';
import {MenuItem} from '../gatewee-navigation/gatewee-navigation.component';
import {Router, RouterLink} from '@angular/router';
import {TuiProgressBar} from '@taiga-ui/kit';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

export type Profile = {
  name: string;
  role: string;
  profilePhoto: string;
  recipient?: Recipient;
};

const tuiDataList = [...TuiDataList] as const;
const tuiDropdown = [...TuiDropdown] as const;

export interface HeaderConfig {
  profile?: Profile | null;
  branding?: PublicSettings['branding'];
  showProjectSelector?: boolean;
  showLanguageSelector?: boolean;
  showThemeToggle?: boolean;
  showMyFeesShortcut?: boolean;
  showTaskbarToggle?: boolean;
  tasks?: TaskWithProgress[] | null;
  tasksRoute?: string;
  onMyFeesNavigate?: () => void;
  onLogout?: () => void;
  onThemeToggle?: () => void;
  onLanguageChange?: (language: string) => void;
  profileMenuAdditionalItems?: MenuItem[];
  /** Route for the profile menu entry. Defaults to `/panel/profile`. */
  profileRoute?: string;
}

@Component({
  selector: 'gatewee-header',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    GateweeIconComponent,

    TuiButton,
    TuiAvatar,
    TuiInitialsPipe,
    TuiAutoColorPipe,
    ...tuiDropdown,
    ...tuiDataList,
    TuiProgressBar,
    RouterLink,
  ],
  templateUrl: './gatewee-header.component.html',
  styleUrls: ['./gatewee-header.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GateweeHeaderComponent implements OnInit, OnChanges {
  private readonly bridge = inject(MobileBridgeService);
  private readonly profileSync = inject(ProfileSyncService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() config: HeaderConfig | null = null;
  @Input() isNavigationCollapsed = false;
  @Output() navigationToggle = new EventEmitter<boolean>();

  isProfileDropdownOpen = false;
  isLanguageDropdownOpen = false;
  isTaskbarDropdownOpen = false;

  /** Only one language dropdown anchor exists: header (desktop) or sub-header (mobile). */
  isMobileLayout = false;

  effectiveProfile: Profile | null = null;

  public readonly devMode = isDevMode();
  logoUrl!: string;

  languageImages: Record<SupportedLanguageCode, string> = {
    pt: 'assets/flags/pt.svg',
    en: 'assets/flags/en.svg',
    es: 'assets/flags/es.svg',
    zh: 'assets/flags/zh.svg',
  };

  get selectedLanguageImage(): string {
    return this.languageImages[this.selectedLanguage];
  }

  get hasActiveTasks(): boolean {
    return (this.config?.tasks ?? []).some(
      t => t.status === 'pending' || t.status === 'processing',
    );
  }

  getTaskLabel(task: TaskWithProgress): string {
    return getTaskLabel(task, (key, params) =>
      this.translate.instant(key, params),
    );
  }

  readonly languageOptions = i18nLanguageOptions;

  selectedLanguage: SupportedLanguageCode = 'pt';

  constructor(
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
  ) {
    this.initializeLanguage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.computeEffectiveProfile();
      this.logoUrl = (this.getLogoUrl() || '') as string;
      this.cdr.markForCheck();
    }
  }

  ngOnInit(): void {
    this.updateMobileLayout();
    this.computeEffectiveProfile();
    this.logoUrl = (this.getLogoUrl() || '') as string;

    this.profileSync.profileUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.computeEffectiveProfile();
        this.cdr.markForCheck();
      });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateMobileLayout();
  }

  private updateMobileLayout(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const next = window.matchMedia('(max-width: 768px)').matches;
    if (this.isMobileLayout !== next) {
      this.isMobileLayout = next;
      this.cdr.markForCheck();
    }
  }

  private computeEffectiveProfile(): void {
    const fromStorage = this.readProfileFromStorage();
    const fromConfig = this.config?.profile;
    const merged = {
      ...(fromStorage ?? {}),
      ...(fromConfig ?? {}),
    } as Profile;
    const next: Profile | null =
      !merged.name && !merged.recipient ? null : merged;

    if (this.profilesEqual(this.effectiveProfile, next)) {
      return;
    }
    this.effectiveProfile = next;
  }

  private profilesEqual(a: Profile | null, b: Profile | null): boolean {
    if (!a && !b) {
      return true;
    }
    if (!a || !b) {
      return false;
    }
    return (
      a.name === b.name &&
      a.role === b.role &&
      a.profilePhoto === b.profilePhoto &&
      JSON.stringify(a.recipient ?? null) ===
        JSON.stringify(b.recipient ?? null)
    );
  }

  private readProfileFromStorage(): Profile | null {
    try {
      const raw = localStorage.getItem('profile');
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as Profile;
    } catch {
      return null;
    }
  }

  get selectedLanguageLabel(): string {
    return (
      this.languageOptions.find(l => l.code === this.selectedLanguage)?.label ??
      'Português'
    );
  }

  get selectedLanguageFlag(): string {
    return (
      this.languageOptions.find(l => l.code === this.selectedLanguage)?.flag ??
      '🇧🇷'
    );
  }

  private initializeLanguage(): void {
    const savedLanguage = localStorage.getItem('language');
    const browserLanguage = navigator.language.split('-')[0];

    this.selectedLanguage = resolveLanguage(savedLanguage, browserLanguage);

    this.translate.setDefaultLang(this.selectedLanguage);
    this.translate.use(this.selectedLanguage);
  }

  private applyLanguage(newLanguage: string): void {
    if (!isSupportedLanguage(newLanguage)) return;
    if (newLanguage === this.selectedLanguage) return;

    localStorage.setItem('language', newLanguage);
    this.selectedLanguage = newLanguage;
    this.translate.use(newLanguage);
    this.cdr.markForCheck();

    if (this.config?.onLanguageChange) {
      this.config.onLanguageChange(newLanguage);
    } else {
      window.location.reload();
    }
  }

  changeLanguage(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const newLanguage = target?.value;
    if (newLanguage) this.applyLanguage(newLanguage);
  }

  changeLanguageByCode(code: SupportedLanguageCode, close?: () => void): void {
    this.applyLanguage(code);
    close?.();
    this.isLanguageDropdownOpen = false;
    this.cdr.markForCheck();
  }

  logout(): void {
    if (this.config?.onLogout) {
      this.config.onLogout();
    }
  }

  navigateToMyFees(): void {
    if (this.config?.onMyFeesNavigate) {
      this.config.onMyFeesNavigate();
    }
    this.isProfileDropdownOpen = false;
    this.cdr.markForCheck();
  }

  toggleTheme(): void {
    if (this.config?.onThemeToggle) {
      this.config.onThemeToggle();

      const newTheme = this.config?.branding?.style?.theme;
      this.logoUrl = (this.getLogoUrl(newTheme) || '') as string;
    } else if (this.config?.branding?.style) {
      this.config.branding.style.theme =
        this.config.branding.style.theme === 'dark' ? 'light' : 'dark';

      const newTheme = this.config.branding.style.theme;
      this.logoUrl = (this.getLogoUrl(newTheme) || '') as string;
    }
    this.cdr.markForCheck();
  }

  toggleProfileDropdown(): void {
    this.isProfileDropdownOpen = !this.isProfileDropdownOpen;
    this.cdr.markForCheck();
  }

  closeProfileDropdown(): void {
    this.isProfileDropdownOpen = false;
    this.cdr.markForCheck();
  }

  toggleNavigation(): void {
    this.navigationToggle.emit(!this.isNavigationCollapsed);
  }

  onLogoClick(): void {
    this.bridge.send({type: MobileBridgeEvent.LOGO_CLICK});
  }

  getLogoUrl(theme?: 'light' | 'dark'): string | null {
    const style = this.config?.branding?.style;
    if (!style) return null;

    const currentTheme = theme || style.theme || 'light';
    const logo = style.logo;
    const logoBlack = style.logoBlack;
    const logoWhite = style.logoWhite;

    if (currentTheme === 'light' && logoBlack) {
      return logoBlack;
    }

    if (currentTheme === 'dark' && logoWhite) {
      return logoWhite;
    }

    return logo || null;
  }

  navigateTo(route: string): void {
    void this.router.navigate([route]);
  }
}
